import { FetchLabDashboardResponse } from '@/src/types/LabDashboard';

export const LAB_DASHBOARD_CACHE_TTL_MS = 60 * 60 * 1000;

export interface LabDashboardCacheEntry {
  response: FetchLabDashboardResponse;
  /** Epoch millis when the response was fetched */
  fetchedAt: number;
}

// Module-scoped so cached months survive LabMetrics remounts (e.g. navigating away and back).
const cache = new Map<string, LabDashboardCacheEntry>();

/** Returns the cached entry for a `YYYY-MM` month, or undefined if missing or older than the TTL. */
export const getCachedLabDashboard = (month: string): LabDashboardCacheEntry | undefined => {
  const entry = cache.get(month);
  if (!entry) {
    return undefined;
  }
  if (Date.now() - entry.fetchedAt >= LAB_DASHBOARD_CACHE_TTL_MS) {
    cache.delete(month);
    return undefined;
  }
  return entry;
};

/** Returns the most recently fetched unexpired entry across all months, evicting expired ones. */
export const getLatestCachedLabDashboard = (): LabDashboardCacheEntry | undefined => {
  let latest: LabDashboardCacheEntry | undefined;
  for (const month of Array.from(cache.keys())) {
    const entry = getCachedLabDashboard(month);
    if (entry && (!latest || entry.fetchedAt >= latest.fetchedAt)) {
      latest = entry;
    }
  }
  return latest;
};

/** Stores a freshly fetched response for a `YYYY-MM` month, replacing any existing entry. */
export const setCachedLabDashboard = (month: string, response: FetchLabDashboardResponse): LabDashboardCacheEntry => {
  const entry: LabDashboardCacheEntry = { response, fetchedAt: Date.now() };
  // Re-insert so Map order is store order; getLatestCachedLabDashboard relies on it to break fetchedAt ties.
  cache.delete(month);
  cache.set(month, entry);
  return entry;
};

export const clearLabDashboardCache = (): void => {
  cache.clear();
};
